import {useEffect, useRef, useState} from 'react';
import {Navigate} from 'react-router-dom';
import {
    WorkflowLoading,
    WorkflowError,
} from '../../navigationButtonComponents/loansTab/containerApp/LoanWorkflowGuard';
import {IContainerApp} from '../../navigationButtonComponents/loansTab/containerApp/IContainerApp';
import InquiryFee from './fee/inquiryFee';
import InquiryOtp from './otp/inquiryOtp';
import {TInnerState} from './IInquriry';
import InquiryResult from './result/inquiryResult';
import {useDispatch} from 'react-redux';
import {
    TotalInquiryRequest,
    ITotalInquiryResponse,
} from '../../../models/inquiry.model';
import {useMutation, useQueryClient} from 'react-query';
import {TotalInquiry} from '../../../services/inquiry.service';
import {GetLoanInfo} from '../../../services/loan.service';
import {INQUIRY_REQUIRED_OTP, INQUIRY_RESULT} from '../../../config/cacheKey';
import {
    changeRouteType,
    getpervInnerState,
} from '../../../redux/handleBackBtn/handleBackBtnSlice';
import {ApiResultModel} from '../../../models/common.model';
import {useLocation, useNavigate} from 'react-router-dom';
export default function WaitForInquiry({
    referenceKey,
    setStep,
    stepMain,
}: IContainerApp) {
    const navigate = useNavigate();
    const location = useLocation();
    const mounted = useRef(false);
    const [resolvedStep, setResolvedStep] = useState<TInnerState | null>(null);
    const [awaitingStatus, setAwaitingStatus] = useState(false);
    const setInnerState = (state: TInnerState) => {
        if (!mounted.current) return;
        setResolvedStep(state);
        // Inquiry substeps are server-selected; revisiting an older one would
        // only redirect back to the current step.
        navigate(`/loans/request/${state}`, {replace: true});
    };
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const randomKeyForDispatch = Math.random();
    const request = {} as TotalInquiryRequest;
    request.referenceKey = referenceKey!;
    const {
        mutate: InquiryValidationOTPMutation,
        isLoading,
        isError,
    } = useMutation(
        async (request: TotalInquiryRequest) => {
            const result = await TotalInquiry(request);
            if (!result?.isSuccess || !result.data)
                throw new Error('Invalid inquiry response');
            let statusChanged = false;
            if (
                !result.data.requiredOtpValidation &&
                !result.data.inquiries.length
            ) {
                const latest = await GetLoanInfo({referenceKey: referenceKey!});
                if (
                    !latest ||
                    latest.referenceKey !== referenceKey ||
                    !latest.loanRequestStatusId
                ) {
                    throw new Error('Invalid loan workflow response');
                }
                statusChanged = latest.loanRequestStatusId !== stepMain;
            }
            return {...result, statusChanged};
        },
        {
            onSuccess: (
                data: ApiResultModel<ITotalInquiryResponse> & {
                    statusChanged: boolean;
                }
            ) => {
                if (!mounted.current) return;
                if (data) {
                    if (data.data.requiredOtpValidation) {
                        setInnerState('otp');
                        queryClient.setQueryData(
                            INQUIRY_REQUIRED_OTP,
                            data.data.requiredOtpValidation
                        );
                    } else if (data.data.inquiries.length > 0) {
                        queryClient.setQueryData(
                            ['totalInquiry', referenceKey],
                            {isSuccess: data.isSuccess, data: data.data}
                        );
                        setInnerState('inquiryResult');
                        queryClient.setQueryData(
                            INQUIRY_RESULT,
                            data.data.inquiries
                        );
                    } else if (data.statusChanged) {
                        setStep();
                    } else {
                        setAwaitingStatus(true);
                    }
                }
            },
            onError: () => {},
        }
    );
    useEffect(() => {
        mounted.current = true;
        if (stepMain === 'WaitForInquiryPayment') {
            setInnerState('fee');
        } else {
            InquiryValidationOTPMutation(request);
        }
        dispatch(changeRouteType({routeType: 'child'}));
        dispatch(
            getpervInnerState({
                prevInnerState: `inquiry ${randomKeyForDispatch}`,
            })
        );
        return () => {
            mounted.current = false;
        };
    }, []);
    if (isError)
        return (
            <WorkflowError
                retry={() => {
                    InquiryValidationOTPMutation(request);
                }}
            />
        );
    if (isLoading) return <WorkflowLoading />;
    if (awaitingStatus)
        return (
            <div role="status" className="p-5 text-center">
                <p>در حال به‌روزرسانی وضعیت درخواست</p>
                <button type="button" onClick={() => setStep()}>
                    بررسی مجدد
                </button>
            </div>
        );
    if (!resolvedStep) return <WorkflowLoading />;
    if (location.pathname !== `/loans/request/${resolvedStep}`) {
        return <Navigate to={`/loans/request/${resolvedStep}`} replace />;
    }
    if (location.pathname.endsWith('/otp'))
        return (
            <InquiryOtp
                setInnerState={setInnerState}
                referenceKey={referenceKey}
            />
        );
    if (location.pathname.endsWith('/inquiryResult'))
        return (
            <InquiryResult
                referenceKey={referenceKey!}
                setInnerState={setInnerState}
                setStep={setStep}
                stepMain={stepMain}
            />
        );
    return (
        <InquiryFee
            setInnerState={setInnerState}
            setStep={setStep}
            referenceKey={referenceKey}
        />
    );
}
